import { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { metenApi } from "../../services/metenApi";

// One number format across the UI: dot for decimals, comma for thousands.
const money = (value) => (value == null || !Number.isFinite(Number(value)) ? "—"
  : `${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const years = (value, isGreek) => `${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${isGreek ? "έτη" : "years"}`;

/** The investment's name, as the detailed proposal names it. */
export function investmentLabel(summary, isGreek) {
  const tr = (el, en) => (isGreek ? el : en);
  if (summary?.investment_after_grant && summary?.investment_after_vat_recovery) {
    return tr("Επένδυση μετά την επιχορήγηση και την ανάκτηση ΦΠΑ", "Investment after grant and VAT recovery");
  }
  if (summary?.investment_after_grant) return tr("Επένδυση μετά την επιχορήγηση", "Investment after grant");
  if (summary?.investment_after_vat_recovery) return tr("Επένδυση μετά την ανάκτηση ΦΠΑ", "Investment after VAT recovery");
  if (summary?.investment_includes_vat) return tr("Κόστος εγκατάστασης με ΦΠΑ", "Installation cost including VAT");
  return tr("Κόστος εγκατάστασης", "Installation cost");
}

/** The tiles of the card, in reading order: money first, then energy. */
export function studyResultTiles(summary, isGreek) {
  if (!summary) return [];
  const tr = (el, en) => (isGreek ? el : en);
  const payback = summary.payback_years != null
    ? years(summary.payback_years, isGreek)
    : summary.payback_not_reached ? tr("Δεν επιτυγχάνεται", "Not reached") : "—";
  const discount = summary.discount_rate_text
    ? tr(`μελέτη χωρίς ΦΠΑ, προεξόφληση ${summary.discount_rate_text}`, `study excl. VAT, discounted at ${summary.discount_rate_text}`)
    : tr("μελέτη χωρίς ΦΠΑ", "study excl. VAT");
  return [
    { key: "investment", label: investmentLabel(summary, isGreek), value: money(summary.investment_eur) },
    { key: "saving", label: tr("Όφελος 1ου έτους", "Year-1 benefit"), value: money(summary.saving_year1_eur) },
    { key: "payback", label: tr("Απόσβεση", "Payback"), value: payback,
      note: summary.payback_beyond_horizon ? tr("πέρα από τη διάρκεια της μελέτης", "beyond the study horizon") : undefined },
    { key: "irr", label: "IRR", value: summary.irr_text || "—", note: tr("μελέτη χωρίς ΦΠΑ", "study excl. VAT") },
    { key: "npv", label: "NPV", value: money(summary.npv_eur), note: discount },
    { key: "selfConsumption", label: tr("Αυτοκατανάλωση", "Self-consumption"), value: summary.self_consumption_text || "—" },
    { key: "selfSufficiency", label: tr("Αυτάρκεια", "Self-sufficiency"), value: summary.self_sufficiency_text || "—" },
    { key: "generation", label: tr("Ετήσια παραγωγή Φ/Β", "Annual PV generation"), value: summary.pv_generation_text || "—" },
  ];
}

function errorText(error, isGreek) {
  const status = error?.status;
  if (status === 404 || status === 409) {
    return isGreek
      ? "Τα αποτελέσματα δεν είναι διαθέσιμα για αυτή την εκτέλεση. Εκτελέστε ξανά τη μελέτη."
      : "The results are not available for this run. Run the study again.";
  }
  if (status === 422) {
    let detail = "";
    try { detail = JSON.parse(error.responseText || "{}").detail || ""; } catch { detail = ""; }
    return isGreek
      ? `Τα αποτελέσματα δεν υπολογίστηκαν με τα τρέχοντα στοιχεία προσφοράς${detail ? `: ${detail}` : "."}`
      : `The results could not be prepared with the current offer details${detail ? `: ${detail}` : "."}`;
  }
  return isGreek ? "Δεν ήταν δυνατή η φόρτωση των αποτελεσμάτων. Δοκιμάστε ξανά." : "The results could not be loaded. Try again.";
}

/**
 * The results of the latest successful behind-the-meter study, opened from a
 * button. The figures are those of the detailed commercial proposal (same
 * frozen study, same offer terms), so the card never disagrees with the PDF.
 * The two main documents can be downloaded from here; every report button on
 * the page stays where it is.
 */
export default function StudyResultsDialog({
  open, onClose, isGreek, projectId, terms, stale, staleText,
  onDownloadProposal, onDownloadTechnical, proposalBusy, technicalBusy, feedback,
}) {
  const tr = (el, en) => (isGreek ? el : en);
  const [state, setState] = useState({ loading: false, error: "", summary: null });
  const termsKey = JSON.stringify(terms || null);
  useEffect(() => {
    if (!open || !projectId) return undefined;
    let alive = true;
    setState({ loading: true, error: "", summary: null });
    metenApi.getCommercialOfferSummary(String(projectId), JSON.parse(termsKey))
      .then((summary) => { if (alive) setState({ loading: false, error: "", summary }); })
      .catch((error) => { if (alive) setState({ loading: false, error: errorText(error, isGreek), summary: null }); });
    return () => { alive = false; };
  }, [open, projectId, termsKey, isGreek]);

  const tiles = studyResultTiles(state.summary, isGreek);
  const busy = Boolean(proposalBusy || technicalBusy);
  const proposalFeedback = feedback && ["proposal", "proposal-docx"].includes(feedback.kind) && (feedback.error || feedback.success)
    ? feedback : null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="study-results-title">
      <DialogTitle id="study-results-title">{tr("Αποτελέσματα μελέτης", "Study results")}</DialogTitle>
      <DialogContent dividers>
        {stale ? <Alert severity="warning" sx={{ mb: 2 }}>{staleText}</Alert> : null}
        {state.loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">{tr("Φόρτωση αποτελεσμάτων…", "Loading results…")}</Typography>
          </Box>
        ) : null}
        {state.error ? <Alert severity="error">{state.error}</Alert> : null}
        {state.summary ? (
          <>
            <Box data-testid="study-results-tiles" sx={{
              display: "grid", gap: 1.5,
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
            }}>
              {tiles.map((tile) => (
                <Box key={tile.key} data-testid={`study-result-${tile.key}`}
                  sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 1.5, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.3 }}>{tile.label}</Typography>
                  <Typography variant="h6" component="p" sx={{ fontWeight: 700, lineHeight: 1.3, mt: 0.5, wordBreak: "break-word" }}>{tile.value}</Typography>
                  {tile.note ? <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>{tile.note}</Typography> : null}
                </Box>
              ))}
            </Box>
            {(state.summary.warnings || []).map((warning) => (
              <Alert key={warning} severity="warning" sx={{ mt: 1.5 }}>{warning}</Alert>
            ))}
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.5 }}>
              {tr("Τα ποσά είναι όπως στην Αναλυτική εμπορική προσφορά, με τα τρέχοντα στοιχεία προσφοράς (τιμές, ΦΠΑ, επιχορήγηση).",
                "The figures are as in the detailed commercial proposal, with the current offer details (prices, VAT, grant).")}
            </Typography>
          </>
        ) : null}
        {proposalFeedback ? (
          <Alert severity={proposalFeedback.error ? "error" : "success"} sx={{ mt: 1.5 }}>
            {proposalFeedback.error || proposalFeedback.success}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mr: "auto" }}>
          <Button variant="contained" disabled={busy} onClick={() => onDownloadProposal({ kind: "proposal" })}
            startIcon={proposalBusy ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}>
            {tr("Αναλυτική εμπορική προσφορά (PDF)", "Detailed commercial proposal (PDF)")}
          </Button>
          <Button variant="outlined" disabled={busy} onClick={() => onDownloadProposal({ kind: "proposal", format: "docx" })}
            startIcon={<ArticleOutlinedIcon />}>
            {tr("Σε Word", "As Word")}
          </Button>
          <Button variant="contained" disabled={busy} onClick={onDownloadTechnical}
            startIcon={technicalBusy ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}>
            {tr("Τεχνική πρόταση (PDF)", "Technical proposal (PDF)")}
          </Button>
        </Stack>
        <Button onClick={onClose}>{tr("Κλείσιμο", "Close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
